/**
 * Alert Preferences Store
 *
 * Manages the current user's notification preferences — which channels
 * (email, SMS) and which alert events they want to receive.
 *
 * Reads/writes the app.alert_preferences table via Supabase client.
 * RLS ensures users can only access their own preferences.
 *
 * @module stores/alertPreferences
 */
import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import { supabase } from '@/lib/supabaseClient'
import { createLogger } from '@/lib/logger'

const log = createLogger('AlertPreferencesStore')

/** All alert event keys that a user can subscribe to.
 * Organized by outbound → return lifecycle to match the Testdrive Roadmap.
 */
export const ALERT_EVENT_OPTIONS = [
  // ── Outbound lifecycle ──
  { key: 'carrier_accepted', label: 'Carrier Accepted', description: 'Carrier has picked up or scanned the outbound package' },
  { key: 'in_transit', label: 'In Transit', description: 'Outbound package is in transit to the consignee' },
  { key: 'out_for_delivery', label: 'Out for Delivery', description: 'Package is on the truck for final-mile delivery' },
  { key: 'delivered_to_borrower', label: 'Delivered to Consignee', description: 'Equipment delivered to the recipient' },
  // ── Return lifecycle ──
  { key: 'return_upcoming', label: 'Return Due Soon', description: 'Reminder before the return due date' },
  { key: 'return_overdue', label: 'Return Overdue', description: 'Return date has passed without a carrier scan' },
  { key: 'return_carrier_scan', label: 'Return Carrier Accepted', description: 'Carrier has scanned/picked up the return package' },
  { key: 'return_in_transit', label: 'Return In Transit', description: 'Return shipment is moving back to the warehouse' },
  { key: 'returned_to_warehouse', label: 'Received at Warehouse', description: 'Return shipment arrived at the warehouse' },
  // ── Other ──
  { key: 'borrower_replied', label: 'Recipient Replied', description: 'When a recipient replies to a notification email' },
  { key: 'checked_in', label: 'Checked In', description: 'Equipment has been inspected and checked back into inventory' },
] as const

export type AlertEventKey = (typeof ALERT_EVENT_OPTIONS)[number]['key']

export interface AlertPreferences {
  id?: number
  user_id: string
  email: string | null
  phone: string | null
  channel_email: boolean
  channel_sms: boolean
  events: AlertEventKey[]
}

export const useAlertPreferencesStore = defineStore('alertPreferences', () => {
  /** Current user's preferences (null if not yet loaded). */
  const preferences = ref<AlertPreferences | null>(null)
  /** Loading state for async operations. */
  const loading = ref(false)
  /** Error message from last operation, if any. */
  const error = ref<string | null>(null)

  /** Whether preferences have been loaded from DB. */
  const loaded = computed(() => preferences.value !== null)

  /** Whether email channel is enabled. */
  const emailEnabled = computed(() => preferences.value?.channel_email ?? true)

  /** Whether SMS channel is enabled. */
  const smsEnabled = computed(() => preferences.value?.channel_sms ?? false)

  /** Array of enabled event keys. */
  const enabledEvents = computed(() => preferences.value?.events ?? [])

  /**
   * Check if a specific event is enabled for the current user.
   * @param eventKey - The alert event key to check.
   */
  function hasEventEnabled(eventKey: string): boolean {
    return enabledEvents.value.includes(eventKey as AlertEventKey)
  }

  /**
   * Load preferences for a specific user from the DB.
   * Creates default preferences if none exist.
   *
   * @param userId - The Supabase user_id to load for.
   * @param userEmail - The user's email (for defaults).
   */
  async function loadPreferences(userId: string, userEmail?: string) {
    loading.value = true
    error.value = null

    try {
      const { data, error: fetchError } = await supabase
        .schema('app')
        .from('alert_preferences')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle()

      if (fetchError) throw fetchError

      if (data) {
        preferences.value = {
          id: data.id,
          user_id: data.user_id,
          email: data.email,
          phone: data.phone,
          channel_email: data.channel_email,
          channel_sms: data.channel_sms,
          events: (data.events as AlertEventKey[]) ?? [],
        }
        log.debug('Loaded alert preferences', { userId })
      } else {
        // No preferences yet — set defaults (all events, email only)
        preferences.value = {
          user_id: userId,
          email: userEmail ?? null,
          phone: null,
          channel_email: true,
          channel_sms: false,
          events: ALERT_EVENT_OPTIONS.map((e) => e.key) as AlertEventKey[],
        }
        log.info('No preferences found, using defaults', { userId })
      }
    } catch (err: any) {
      error.value = err.message ?? String(err)
      log.error('Failed to load alert preferences', err)
    } finally {
      loading.value = false
    }
  }

  /**
   * Save (upsert) the current preferences to the DB.
   * Creates a new record if none exists, otherwise updates.
   */
  async function savePreferences(updated: Partial<AlertPreferences>) {
    if (!preferences.value) return

    loading.value = true
    error.value = null

    const merged: AlertPreferences = {
      ...preferences.value,
      ...updated,
    }

    try {
      const row = {
        user_id: merged.user_id,
        email: merged.email,
        phone: merged.phone,
        channel_email: merged.channel_email,
        channel_sms: merged.channel_sms,
        events: merged.events,
        updated_at: new Date().toISOString(),
      }

      const { data, error: upsertError } = await supabase
        .schema('app')
        .from('alert_preferences')
        .upsert(row, { onConflict: 'user_id' })
        .select('*')
        .maybeSingle()

      if (upsertError) throw upsertError

      if (data) {
        preferences.value = {
          id: data.id,
          user_id: data.user_id,
          email: data.email,
          phone: data.phone,
          channel_email: data.channel_email,
          channel_sms: data.channel_sms,
          events: (data.events as AlertEventKey[]) ?? [],
        }
      }

      log.info('Alert preferences saved', { userId: merged.user_id })
    } catch (err: any) {
      error.value = err.message ?? String(err)
      log.error('Failed to save alert preferences', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  /** Reset store state (e.g. on sign-out). */
  function $reset() {
    preferences.value = null
    loading.value = false
    error.value = null
  }

  return {
    preferences,
    loading,
    error,
    loaded,
    emailEnabled,
    smsEnabled,
    enabledEvents,
    hasEventEnabled,
    loadPreferences,
    savePreferences,
    $reset,
  }
})
